import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FaArrowLeft,
  FaArrowRight,
  FaBan,
  FaCheck,
  FaDiscord,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaShieldAlt,
  FaSpinner,
  FaTimes,
  FaUser,
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/apiService';
import VihsMark from '../../components/VihsMark';
import {
  AUTH_POLICY,
  evaluatePasswordClient,
  evaluateUsernameClient,
  PASSWORD_CHECK_LABELS,
  USERNAME_CHECK_LABELS,
} from '../../utils/authPolicyClient';
import './Auth.css';

const ACCOUNT_DISABLED_COPY = {
  title: 'Conta desativada',
  lead: 'O acesso a esta conta foi bloqueado pela equipe da Vih\'s.',
  body: 'Isso geralmente acontece quando há indícios de violação dos Termos de Uso ou das Políticas da comunidade — como abuso, fraude, compartilhamento indevido de acesso ou conduta que comprometa a segurança do ecossistema.',
  footer: 'Se você acredita que houve um engano, fale com o suporte oficial da Vih\'s para pedir uma revisão.',
};

const initialForm = {
  username: '',
  email: '',
  password: '',
  passwordConfirm: '',
  acceptTerms: false,
  website: '',
};

const modeContent = {
  login: {
    eyebrow: 'Acesso seguro',
    title: 'Bem-vindo de volta.',
    description: 'Entre para continuar no ecossistema Vih’s.',
    submit: 'Entrar na minha conta',
    loading: 'Autenticando...',
  },
  register: {
    eyebrow: 'Crie seu acesso',
    title: 'Comece sua jornada.',
    description: 'Uma conta para acessar todo o ecossistema Vih’s.',
    submit: 'Criar minha conta',
    loading: 'Criando sua conta...',
  },
  recovery: {
    eyebrow: 'Recuperar acesso',
    title: 'Redefina sua senha.',
    description: 'Enviaremos as instruções para o seu e-mail.',
    submit: 'Enviar instruções',
    loading: 'Enviando...',
  },
  reset: {
    eyebrow: 'Nova senha',
    title: 'Crie uma nova senha.',
    description: 'Escolha uma senha forte para recuperar o acesso.',
    submit: 'Salvar nova senha',
    loading: 'Salvando...',
  },
};

function Field({ icon: Icon, label, error, action, ...inputProps }) {
  return (
    <div className={`auth-field${error ? ' has-error' : ''}`}>
      <div className="auth-label-row">
        <label htmlFor={inputProps.id}>{label}</label>
        {action}
      </div>
      <div className="auth-input-wrap">
        <Icon aria-hidden="true" />
        <input {...inputProps} aria-invalid={Boolean(error)} aria-describedby={error ? `${inputProps.id}-error` : undefined} />
      </div>
      {error && (
        <span id={`${inputProps.id}-error`} className="auth-field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

function PolicyChecklist({ title, evaluation, labels }) {
  return (
    <div className="auth-policy-checklist" aria-live="polite">
      <div className="auth-policy-checklist-head">
        <strong>{title}</strong>
        <span className={`auth-policy-label ${evaluation.ok ? 'is-ok' : ''}`}>{evaluation.label || `${evaluation.score}/${evaluation.max}`}</span>
      </div>
      <ul>
        {Object.entries(labels).map(([key, text]) => (
          <li key={key} className={evaluation.checks[key] ? 'is-met' : ''}>
            <span aria-hidden="true">{evaluation.checks[key] ? <FaCheck /> : <FaTimes />}</span>
            {text}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { login: setAuthUser, isAuthenticated } = useAuth();
  const [mode, setMode] = useState('login');
  const [resetToken, setResetToken] = useState('');
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const content = modeContent[mode];
  const usernameEval = useMemo(() => evaluateUsernameClient(form.username), [form.username]);
  const passwordEval = useMemo(
    () => evaluatePasswordClient(form.password, {
      username: form.username,
      email: form.email,
    }),
    [form.password, form.username, form.email]
  );

  useEffect(() => {
    document.title = `${mode === 'register' ? 'Criar conta' : mode === 'recovery' || mode === 'reset' ? 'Recuperar acesso' : 'Entrar'} — Vih's`;
    return () => {
      document.title = "Vih's — criação digital sob medida";
    };
  }, [mode]);

  useEffect(() => {
    if (isAuthenticated && mode !== 'reset') {
      navigate('/conta', { replace: true });
    }
  }, [isAuthenticated, mode, navigate]);

  useEffect(() => {
    const verify = searchParams.get('verify');
    const reset = searchParams.get('reset');

    if (verify) {
      authService.verifyEmail(verify)
        .then(() => {
          setStatus({ type: 'success', message: 'E-mail verificado com sucesso. Você já pode continuar.' });
          setMode('login');
        })
        .catch((error) => {
          setStatus({ type: 'error', message: error.message || 'Não foi possível verificar o e-mail.' });
        })
        .finally(() => {
          setSearchParams({}, { replace: true });
        });
    }

    if (reset) {
      setResetToken(reset);
      setMode('reset');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return;

    const params = new URLSearchParams(hash);
    const authResult = params.get('auth');
    const token = params.get('token');

    window.history.replaceState(null, '', window.location.pathname);

    if (authResult === 'discord-error') {
      setStatus({ type: 'error', message: 'Não foi possível autenticar com o Discord. Tente novamente.' });
      return;
    }

    if (authResult === 'account-disabled') {
      setStatus({ type: 'disabled' });
      return;
    }

    if (authResult === 'discord' && token) {
      const completeDiscordLogin = async () => {
        setIsLoading(true);
        try {
          await setAuthUser(null, token);
          setStatus({ type: 'success', message: 'Login com Discord concluído. Redirecionando...' });
          window.setTimeout(() => navigate('/conta'), 900);
        } catch (error) {
          setStatus({ type: 'error', message: error.message || 'Falha ao concluir o login com Discord.' });
        } finally {
          setIsLoading(false);
        }
      };

      completeDiscordLogin();
    }
  }, [navigate, setAuthUser]);

  const changeMode = (nextMode) => {
    setMode(nextMode);
    setErrors({});
    setStatus(null);
    setShowPassword(false);
    if (nextMode !== 'reset') setResetToken('');
  };

  const handleChange = ({ target }) => {
    const { name, type, value, checked } = target;
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
    setStatus(null);
  };

  const validate = () => {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    if (mode !== 'reset' && !emailPattern.test(form.email.trim())) {
      nextErrors.email = 'Informe um e-mail válido.';
    }

    if (mode === 'register') {
      if (!usernameEval.ok) {
        nextErrors.username = `Use pelo menos ${AUTH_POLICY.USERNAME_MIN} caracteres, começando com letra.`;
      }
      if (!passwordEval.ok) nextErrors.password = 'Crie uma senha que atenda a todos os requisitos.';
      if (form.password !== form.passwordConfirm) nextErrors.passwordConfirm = 'As senhas não coincidem.';
      if (!form.acceptTerms) nextErrors.acceptTerms = 'Você precisa aceitar os termos para continuar.';
    } else if (mode === 'login' && !form.password) {
      nextErrors.password = 'Digite sua senha.';
    } else if (mode === 'reset') {
      if (!passwordEval.ok) nextErrors.password = 'Crie uma senha que atenda a todos os requisitos.';
      if (form.password !== form.passwordConfirm) nextErrors.passwordConfirm = 'As senhas não coincidem.';
      if (!resetToken) nextErrors.password = 'Token de redefinição inválido.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    setStatus(null);

    try {
      if (mode === 'recovery') {
        const result = await authService.forgotPassword(form.email.trim());
        const debugHint = result?.debugResetToken
          ? ` (dev token: ${result.debugResetToken})`
          : '';
        setStatus({
          type: 'success',
          message: `Se existir uma conta com este e-mail, enviaremos instruções em breve.${debugHint}`,
        });
        return;
      }

      if (mode === 'reset') {
        const response = await authService.resetPassword(resetToken, {
          password: form.password,
          passwordConfirm: form.passwordConfirm,
        });
        await setAuthUser(response?.data?.user || null, response?.token);
        setStatus({ type: 'success', message: 'Senha redefinida. Redirecionando...' });
        window.setTimeout(() => navigate('/conta'), 900);
        return;
      }

      const response = mode === 'login'
        ? await authService.login({
            email: form.email.trim(),
            password: form.password,
            website: form.website,
          })
        : await authService.register({
            username: form.username.trim(),
            email: form.email.trim(),
            password: form.password,
            passwordConfirm: form.passwordConfirm,
            website: form.website,
          });

      await setAuthUser(response?.data?.user || null, response?.token);
      setStatus({
        type: 'success',
        message: mode === 'register'
          ? 'Conta criada. Confirme o código enviado ao seu e-mail...'
          : 'Login realizado. Redirecionando...',
      });
      window.setTimeout(() => navigate('/conta'), 900);
    } catch (error) {
      if (error.code === 'ACCOUNT_DISABLED') {
        setStatus({ type: 'disabled' });
      } else {
        setStatus({ type: 'error', message: error.message || 'Ocorreu um erro inesperado.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const discordUrl = authService.getDiscordAuthUrl();
  const showSocial = mode === 'login' || mode === 'register';
  const showTabs = mode === 'login' || mode === 'register';

  return (
    <main className="auth-page">
      <div className="auth-atmosphere" aria-hidden="true">
        <div className="auth-orb auth-orb-one" />
        <div className="auth-orb auth-orb-two" />
        <div className="auth-grid" />
      </div>

      <header className="auth-topbar">
        <Link to="/" className="auth-brand" aria-label="Vih's — página inicial">
          <VihsMark />
          Vih&apos;s
        </Link>
        <Link to="/" className="auth-back">
          <FaArrowLeft aria-hidden="true" />
          Voltar ao início
        </Link>
      </header>

      <div className="auth-layout">
        <motion.section
          className="auth-presentation"
          initial={{ opacity: 0, x: -28 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="auth-presentation-tag">
            <FaShieldAlt aria-hidden="true" />
            Vih&apos;s ID
          </span>
          <h1>
            Um acesso.
            <br />
            <span>Todo o ecossistema.</span>
          </h1>
          <p>
            Conecte-se ao Studio, Bot e Dev Hub com uma identidade única, simples e segura.
          </p>
          <ul className="auth-benefits">
            <li><span><FaCheck /></span> Proteção anti-brute-force e rate limit</li>
            <li><span><FaCheck /></span> Senha forte obrigatória e sessões controladas</li>
            <li><span><FaCheck /></span> Integração nativa com Discord</li>
          </ul>
        </motion.section>

        <motion.section
          className="auth-card"
          initial={{ opacity: 0, y: 24, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          aria-labelledby="auth-title"
        >
          {showTabs && (
            <div className="auth-tabs" role="tablist" aria-label="Acesso à conta">
              <button type="button" className={mode === 'login' ? 'active' : ''} onClick={() => changeMode('login')} role="tab" aria-selected={mode === 'login'}>
                Entrar
              </button>
              <button type="button" className={mode === 'register' ? 'active' : ''} onClick={() => changeMode('register')} role="tab" aria-selected={mode === 'register'}>
                Criar conta
              </button>
            </div>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              <div className="auth-card-heading">
                <span>{content.eyebrow}</span>
                <h2 id="auth-title">{content.title}</h2>
                <p>{content.description}</p>
              </div>

              {showSocial && (
                <>
                  <a className="auth-discord" href={discordUrl}>
                    <FaDiscord aria-hidden="true" />
                    Continuar com Discord
                  </a>
                  <div className="auth-divider"><span>ou continue com e-mail</span></div>
                </>
              )}

              <form className="auth-form" onSubmit={handleSubmit} noValidate autoComplete="on">
                <div className="auth-honeypot" aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={handleChange}
                  />
                </div>

                {mode === 'register' && (
                  <>
                    <Field
                      icon={FaUser}
                      label="Nome de usuário"
                      id="username"
                      name="username"
                      type="text"
                      autoComplete="username"
                      placeholder={`Mínimo ${AUTH_POLICY.USERNAME_MIN} caracteres`}
                      value={form.username}
                      onChange={handleChange}
                      maxLength={AUTH_POLICY.USERNAME_MAX}
                      error={errors.username}
                    />
                    {form.username.length > 0 && (
                      <PolicyChecklist
                        title="Requisitos do usuário"
                        evaluation={{ ...usernameEval, label: usernameEval.ok ? 'Válido' : 'Incompleto' }}
                        labels={USERNAME_CHECK_LABELS}
                      />
                    )}
                  </>
                )}

                {mode !== 'reset' && (
                  <Field
                    icon={FaEnvelope}
                    label="E-mail"
                    id="email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="voce@exemplo.com"
                    value={form.email}
                    onChange={handleChange}
                    error={errors.email}
                  />
                )}

                {(mode === 'login' || mode === 'register' || mode === 'reset') && (
                  <Field
                    icon={FaLock}
                    label={mode === 'reset' ? 'Nova senha' : 'Senha'}
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    placeholder={mode === 'login' ? 'Digite sua senha' : `Mínimo ${AUTH_POLICY.PASSWORD_MIN} caracteres`}
                    value={form.password}
                    onChange={handleChange}
                    error={errors.password}
                    action={mode === 'login' ? (
                      <button type="button" className="auth-forgot" onClick={() => changeMode('recovery')}>
                        Esqueci minha senha
                      </button>
                    ) : undefined}
                  />
                )}

                {(mode === 'register' || mode === 'reset') && (
                  <>
                    {form.password.length > 0 && (
                      <>
                        <div className="auth-strength" aria-label={`Força da senha: ${passwordEval.label}`}>
                          <div>
                            {[1, 2, 3, 4, 5, 6, 7].map((level) => (
                              <span
                                key={level}
                                className={`${passwordEval.score >= level ? 'filled' : ''} ${passwordEval.ok && level === 7 ? 'is-excellent' : ''}`}
                              />
                            ))}
                          </div>
                          <p>{passwordEval.label} · política de engenharia aplicada.</p>
                        </div>
                        <PolicyChecklist
                          title="Requisitos da senha"
                          evaluation={passwordEval}
                          labels={PASSWORD_CHECK_LABELS}
                        />
                      </>
                    )}
                    <Field
                      icon={FaLock}
                      label="Confirmar senha"
                      id="passwordConfirm"
                      name="passwordConfirm"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="Repita sua senha"
                      value={form.passwordConfirm}
                      onChange={handleChange}
                      error={errors.passwordConfirm}
                    />
                  </>
                )}

                {(mode === 'login' || mode === 'register' || mode === 'reset') && (
                  <label className="auth-show-password">
                    <input type="checkbox" checked={showPassword} onChange={() => setShowPassword((visible) => !visible)} />
                    <span>{showPassword ? <FaEyeSlash /> : <FaEye />}</span>
                    {showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  </label>
                )}

                {mode === 'register' && (
                  <div>
                    <label className={`auth-terms-check${errors.acceptTerms ? ' has-error' : ''}`}>
                      <input name="acceptTerms" type="checkbox" checked={form.acceptTerms} onChange={handleChange} />
                      <span aria-hidden="true"><FaCheck /></span>
                      <em>Li e aceito os <Link to="/termos">Termos de Uso e Política de Privacidade</Link>.</em>
                    </label>
                    {errors.acceptTerms && <p className="auth-field-error">{errors.acceptTerms}</p>}
                  </div>
                )}

                <AnimatePresence>
                  {status && status.type !== 'disabled' && (
                    <motion.div
                      className={`auth-alert ${status.type}`}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      role="status"
                    >
                      {status.message}
                    </motion.div>
                  )}
                </AnimatePresence>

                <button className="auth-submit" type="submit" disabled={isLoading}>
                  {isLoading ? <FaSpinner className="auth-spinner" aria-hidden="true" /> : <FaArrowRight aria-hidden="true" />}
                  <span>{isLoading ? content.loading : content.submit}</span>
                </button>
              </form>

              {(mode === 'recovery' || mode === 'reset') && (
                <button type="button" className="auth-return-login" onClick={() => changeMode('login')}>
                  <FaArrowLeft aria-hidden="true" />
                  Voltar para o login
                </button>
              )}
            </motion.div>
          </AnimatePresence>

          <p className="auth-security-note">
            <FaShieldAlt aria-hidden="true" />
            Seus dados são protegidos e nunca serão compartilhados.
          </p>
        </motion.section>
      </div>

      <AnimatePresence>
        {status?.type === 'disabled' && (
          <motion.div
            className="auth-disabled-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setStatus(null)}
          >
            <motion.div
              className="auth-disabled-modal"
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="auth-disabled-title"
            >
              <button
                type="button"
                className="auth-disabled-close"
                onClick={() => setStatus(null)}
                aria-label="Fechar"
              >
                <FaTimes />
              </button>

              <div className="auth-disabled-icon" aria-hidden="true">
                <FaBan />
              </div>

              <p className="auth-disabled-eyebrow">Acesso bloqueado</p>
              <h2 id="auth-disabled-title">{ACCOUNT_DISABLED_COPY.title}</h2>
              <p className="auth-disabled-lead">{ACCOUNT_DISABLED_COPY.lead}</p>
              <p>{ACCOUNT_DISABLED_COPY.body}</p>
              <p>{ACCOUNT_DISABLED_COPY.footer}</p>

              <div className="auth-disabled-actions">
                <Link to="/termos" className="auth-disabled-link">
                  Revisar Termos e Políticas
                </Link>
                <button type="button" className="auth-disabled-ok" onClick={() => setStatus(null)}>
                  Entendi
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
